import React, { createContext, useContext, useState } from 'react';
import { Dialog, DialogProps, Box, BoxProps, Divider } from '@mui/material';
import { Text, TextProps } from 'src/components/BeaconV2Components/Generic/Text';
import { CloseIcon } from 'src/components/SvgIcons';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';

export interface SlDialogContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

const SlDialogContext = createContext<SlDialogContextValue>(null);

export const useSlDialogContext = () => {
  const context = useContext(SlDialogContext);

  if (!context) {
    throw new Error('SlDialogContext must be used within a SlDialog');
  }

  return context;
};

export interface SlDialogProps extends Omit<DialogProps, 'open'> {
  width?: string | number;
  height?: string | number;
  open?: boolean;
}

export function SlDialogRoot({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  const dialogContext = {
    isOpen,
    open,
    close
  };

  return <SlDialogContext.Provider value={dialogContext}>{children}</SlDialogContext.Provider>;
}

export const SlDialog = ({ sx, width, height, onClose, ...rest }: SlDialogProps) => {
  const { isOpen, close } = useSlDialogContext();

  return (
    <Dialog
      open={isOpen}
      onClose={(event) => {
        close();
        // eslint-disable-next-line no-unused-expressions
        onClose?.(event, 'backdropClick');
      }}
      sx={{
        ...{
          '& .MuiPaper-root': {
            boxShadow: '0 0 16px 0 rgba(0, 0, 0, 0.06)',
            border: 'solid 1px #dedede',
            backgroundColor: '#fff',
            borderRadius: '8px',
            height,
            width,
            display: 'flex',
            flexDirection: 'column'
          }
        },
        ...sx
      }}
      {...rest}
    />
  );
};

export type SlDialogHeaderProps = BoxProps;
export function SlDialogHeader({ sx, ...rest }: SlDialogHeaderProps) {
  return (
    <Flex flexDirection="column">
      <Box
        sx={{ ...{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }, ...sx }}
        {...rest}
      />
      <Divider />
    </Flex>
  );
}

export type SlDialogTitleProps = TextProps;
export function SlDialogTitle({ sx, ...rest }: SlDialogTitleProps) {
  return <Text variant="h4" sx={sx} {...rest} />;
}

export type SlDialogCloseButtonProps = React.ComponentProps<typeof CloseIcon>;
// eslint-disable-next-line react/prop-types
export function SlDialogCloseButton({ style, onClick, ...rest }: SlDialogCloseButtonProps) {
  const { close } = useSlDialogContext();
  return (
    <CloseIcon
      style={{
        position: 'absolute',
        right: '24px',
        top: '24px',
        cursor: 'pointer',
        width: '24px',
        height: '24px',
        ...style
      }}
      onClick={(event) => {
        close();
        // eslint-disable-next-line no-unused-expressions
        onClick?.(event);
      }}
      {...rest}
    />
  );
}

export type SlDialogContentProps = BoxProps;
export function SlDialogContent({ sx, ...rest }: SlDialogContentProps) {
  return (
    <Box
      sx={{
        flex: 1,
        paddingTop: '24px',
        paddingBottom: '24px',
        paddingLeft: '48px',
        paddingRight: '48px',
        overflow: 'scroll',
        ...sx
      }}
      {...rest}
    />
  );
}

export type SlDialogFooterProps = BoxProps;
export function SlDialogFooter({ sx, ...rest }: SlDialogFooterProps) {
  return (
    <Box>
      <Divider />
      <Box sx={{ ...{ padding: '20px 48px' }, ...sx }} {...rest} />
    </Box>
  );
}

export type SlDialogTriggerProps = React.HTMLAttributes<HTMLSpanElement>;
export const SlDialogTrigger = React.forwardRef<HTMLSpanElement, SlDialogTriggerProps>(function SlDialogTrigger(
  // eslint-disable-next-line react/prop-types
  { onClick, ...rest },
  ref
) {
  const { open } = useSlDialogContext();

  return (
    <span
      ref={ref}
      onClick={(event) => {
        open();
        // eslint-disable-next-line no-unused-expressions
        onClick?.(event);
      }}
      role="button"
      {...rest}
    />
  );
});
